import { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faChartLine,
  faDice,
  faScaleBalanced,
  faShieldHalved,
  faPercent,
  faArrowRight,
  faInfoCircle,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { usePageMeta } from '../../hooks/usePageMeta';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import {
  calculate,
  type CalculatorInputs,
  type CalculatorResults,
} from '../../utils/strategyCalculator';

// ── Scroll-reveal hook (intersection observer) ──────────────────────────────

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, className: `transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}` };
}

// ── Default inputs ──────────────────────────────────────────────────────────

const DEFAULTS: CalculatorInputs = {
  winRate: 0.55,
  riskRewardRatio: 2.0,
  numberOfTrades: 100,
  riskPerTrade: 0.02,
  startingCapital: 10000,
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 2): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtPct(n: number, decimals = 1): string {
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtDollar(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ── Slider Input Component ──────────────────────────────────────────────────

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  onChange: (v: number) => void;
  tooltip?: string;
}

function Slider({ label, value, min, max, step, displayValue, onChange, tooltip }: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-300 flex items-center gap-1.5">
          {label}
          {tooltip && (
            <span className="group relative">
              <FontAwesomeIcon icon={faInfoCircle} className="text-gray-500 text-[11px] cursor-help" />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-gray-200 bg-gray-800 border border-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {tooltip}
              </span>
            </span>
          )}
        </label>
        <span className="text-sm font-mono text-white tabular-nums">{displayValue}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                   [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4
                   [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full
                   [&::-webkit-slider-thumb]:bg-[#9d4edd] [&::-webkit-slider-thumb]:border-2
                   [&::-webkit-slider-thumb]:border-white/20 [&::-webkit-slider-thumb]:cursor-pointer
                   [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
                   [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#9d4edd]
                   [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white/20"
        style={{
          background: `linear-gradient(to right, #9d4edd ${pct}%, #1f2937 ${pct}%)`,
        }}
      />
    </div>
  );
}

// ── Metric Card ─────────────────────────────────────────────────────────────

interface MetricCardProps {
  icon: typeof faCalculator;
  label: string;
  value: string;
  sub?: string;
  positive?: boolean;
  negative?: boolean;
}

function MetricCard({ icon, label, value, sub, positive, negative }: MetricCardProps) {
  const color = positive ? 'text-emerald-400' : negative ? 'text-red-400' : 'text-white';
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 space-y-2">
      <div className="flex items-center gap-2 text-gray-400 text-sm">
        <FontAwesomeIcon icon={icon} className="text-[#9d4edd] text-xs" />
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

// ── Monte Carlo Chart (recharts) ────────────────────────────────────────────

const CURVE_COLORS = ['#ef4444', '#f59e0b', '#9d4edd', '#22c55e', '#3b82f6'];
const CURVE_KEYS = ['p5', 'p25', 'p50', 'p75', 'p95'] as const;
const CURVE_LABELS = ['Worst 5%', '25th pct', 'Median', '75th pct', 'Best 5%'];

function buildChartData(results: CalculatorResults) {
  const { percentiles } = results.monteCarlo;
  if (!percentiles.length) return [];
  return percentiles[0].map((_, i) => ({
    trade: i,
    p5: percentiles[0][i],
    p25: percentiles[1][i],
    p50: percentiles[2][i],
    p75: percentiles[3][i],
    p95: percentiles[4][i],
  }));
}

function MonteCarloChart({ results }: { results: CalculatorResults }) {
  const data = buildChartData(results);
  if (!data.length) return null;

  const startingCapital = data[0].p50;

  return (
    <div className="space-y-3">
      <div className="h-[340px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 16, left: 8, bottom: 4 }}>
            <defs>
              {CURVE_KEYS.map((key, i) => (
                <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CURVE_COLORS[i]} stopOpacity={key === 'p50' ? 0.25 : 0.1} />
                  <stop offset="95%" stopColor={CURVE_COLORS[i]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" strokeOpacity={0.8} />
            <XAxis
              dataKey="trade"
              stroke="#4b5563"
              tick={{ fill: '#6b7280', fontSize: 11 }}
              label={{ value: 'Trade Number', position: 'insideBottom', offset: -2, fill: '#6b7280', fontSize: 11 }}
            />
            <YAxis
              stroke="#4b5563"
              tick={{ fill: '#6b7280', fontSize: 11 }}
              tickFormatter={(v: number) => fmtDollar(v)}
              width={72}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }}
              labelFormatter={(v: number) => `Trade ${v}`}
              formatter={(value: number, name: string) => {
                const label = CURVE_LABELS[CURVE_KEYS.indexOf(name as typeof CURVE_KEYS[number])] ?? name;
                return [fmtDollar(value), label];
              }}
            />
            <ReferenceLine y={startingCapital} stroke="#6b7280" strokeDasharray="6 3" strokeWidth={1} />
            {CURVE_KEYS.map((key, i) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={CURVE_COLORS[i]}
                strokeWidth={key === 'p50' ? 2.5 : 1.5}
                strokeOpacity={key === 'p50' ? 1 : 0.7}
                fill={`url(#grad-${key})`}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-x-5 gap-y-1">
        {CURVE_LABELS.map((label, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className="inline-block w-4 h-0.5 rounded" style={{ backgroundColor: CURVE_COLORS[i] }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page Component ─────────────────────────────────────────────────────

export default function StrategyCalculatorPage() {
  usePageMeta({
    title: 'Strategy Performance Calculator | Quanthop Labs',
    description:
      'Free trading calculator. Compute expectancy, probability of ruin, Kelly criterion, and run Monte Carlo simulations on any win-rate and risk-reward profile.',
  });

  // JSON-LD structured data for SEO
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Strategy Performance Calculator',
      url: 'https://quanthop.com/labs/strategy-performance-calculator',
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'All',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      description:
        'Free trading strategy calculator. Compute expectancy, Kelly criterion, probability of ruin, and run Monte Carlo simulations.',
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULTS);
  const [results, setResults] = useState<CalculatorResults | null>(null);

  const handleCalculate = useCallback(() => {
    setResults(calculate(inputs));
  }, [inputs]);

  // Auto-calculate on first render
  useEffect(() => {
    setResults(calculate(DEFAULTS));
  }, []);

  const update = (field: keyof CalculatorInputs, value: number) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const revHero = useScrollReveal(0.1);
  const revEducation = useScrollReveal(0.1);
  const revCta = useScrollReveal(0.1);

  const isPositiveEdge = results ? results.expectancy > 0 : false;

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins]">
      <PublicNavBar />

      {/* ── Hero Section ──────────────────────────────────────────────── */}
      <section
        ref={revHero.ref}
        className={`relative overflow-hidden pt-32 pb-16 px-4 sm:px-6 ${revHero.className}`}
      >
        <div className="max-w-4xl mx-auto text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9d4edd]/10 border border-[#9d4edd]/20 text-[#c77dff] text-xs font-medium">
            <FontAwesomeIcon icon={faCalculator} className="text-[10px]" />
            Free Tool
          </div>
          <h1 className="text-3xl md:text-[42px] font-bold text-white leading-tight">
            Strategy Performance Calculator
          </h1>
          <p className="text-gray-400/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Enter your strategy&apos;s win rate and risk-reward ratio to instantly compute
            expectancy, Kelly criterion, probability of ruin, and run 1,000 Monte Carlo
            simulations — no signup required.
          </p>
        </div>
      </section>

      {/* ── Calculator Section ────────────────────────────────────────── */}
      <section className="pb-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
            {/* ── Input Panel ── */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 space-y-6 h-fit lg:sticky lg:top-24">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FontAwesomeIcon icon={faScaleBalanced} className="text-[#9d4edd] text-sm" />
                Parameters
              </h2>

              <Slider
                label="Win Rate"
                value={inputs.winRate}
                min={0.05} max={0.95} step={0.01}
                displayValue={fmtPct(inputs.winRate, 0)}
                onChange={(v) => update('winRate', v)}
                tooltip="Percentage of trades that are profitable"
              />
              <Slider
                label="Risk / Reward Ratio"
                value={inputs.riskRewardRatio}
                min={0.1} max={10} step={0.1}
                displayValue={`1 : ${fmt(inputs.riskRewardRatio, 1)}`}
                onChange={(v) => update('riskRewardRatio', v)}
                tooltip="Average win size divided by average loss size"
              />
              <Slider
                label="Risk per Trade"
                value={inputs.riskPerTrade}
                min={0.005} max={0.1} step={0.005}
                displayValue={fmtPct(inputs.riskPerTrade, 1)}
                onChange={(v) => update('riskPerTrade', v)}
                tooltip="Fraction of capital risked on each trade"
              />
              <Slider
                label="Number of Trades"
                value={inputs.numberOfTrades}
                min={10} max={500} step={10}
                displayValue={inputs.numberOfTrades.toString()}
                onChange={(v) => update('numberOfTrades', v)}
                tooltip="Trades per simulation run"
              />
              <Slider
                label="Starting Capital"
                value={inputs.startingCapital}
                min={1000} max={100000} step={1000}
                displayValue={fmtDollar(inputs.startingCapital)}
                onChange={(v) => update('startingCapital', v)}
              />

              <button
                onClick={handleCalculate}
                className="w-full px-6 py-3 rounded-xl bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faCalculator} className="text-xs" />
                Calculate
              </button>
            </div>

            {/* ── Results Panel ── */}
            <div className="space-y-8">
              {results && (
                <>
                  {/* Edge summary */}
                  <div>
                    <div className={`rounded-2xl border p-5 ${isPositiveEdge
                      ? 'bg-emerald-500/[0.04] border-emerald-500/20'
                      : 'bg-red-500/[0.04] border-red-500/20'
                      }`}>
                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon
                          icon={isPositiveEdge ? faChartLine : faTriangleExclamation}
                          className={`mt-0.5 ${isPositiveEdge ? 'text-emerald-400' : 'text-red-400'}`}
                        />
                        <div>
                          <div className={`text-sm font-semibold ${isPositiveEdge ? 'text-emerald-400' : 'text-red-400'}`}>
                            {isPositiveEdge ? 'Positive Edge Detected' : 'Negative Expectancy'}
                          </div>
                          <p className="text-sm text-gray-400 mt-1">
                            {isPositiveEdge
                              ? `For every $1 risked you expect to make $${fmt(results.expectancy)}. Over ${inputs.numberOfTrades} trades, the expected return is ${fmtDollar(results.expectedReturn)} (${fmt(results.expectedReturnPct, 1)}%).`
                              : `This parameter combination loses money over time. You need a win rate above ${fmtPct(results.breakEvenWinRate)} to break even with a ${fmt(inputs.riskRewardRatio, 1)}:1 reward ratio.`
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key metrics grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <MetricCard
                      icon={faChartLine} label="Expectancy"
                      value={`$${fmt(results.expectancy)}`}
                      sub="Per $1 risked"
                      positive={results.expectancy > 0}
                      negative={results.expectancy <= 0}
                    />
                    <MetricCard
                      icon={faPercent} label="Profit Factor"
                      value={results.profitFactor === Infinity ? 'Inf' : fmt(results.profitFactor)}
                      sub={`Break-even: ${fmtPct(results.breakEvenWinRate)}`}
                      positive={results.profitFactor > 1}
                      negative={results.profitFactor <= 1}
                    />
                    <MetricCard
                      icon={faScaleBalanced} label="Kelly Criterion"
                      value={fmtPct(results.kellyFraction)}
                      sub={`Half-Kelly: ${fmtPct(results.halfKelly)}`}
                    />
                    <MetricCard
                      icon={faShieldHalved} label="Ruin Probability"
                      value={fmtPct(results.probabilityOfRuin, 2)}
                      sub={`${results.monteCarlo.ruinCount} / ${results.monteCarlo.totalSimulations} sims ruined`}
                      positive={results.probabilityOfRuin < 0.05}
                      negative={results.probabilityOfRuin > 0.2}
                    />
                    <MetricCard
                      icon={faDice} label="Median Outcome"
                      value={fmtDollar(results.monteCarlo.medianFinalEquity)}
                      sub={`Mean: ${fmtDollar(results.monteCarlo.meanFinalEquity)}`}
                      positive={results.monteCarlo.medianFinalEquity > inputs.startingCapital}
                      negative={results.monteCarlo.medianFinalEquity < inputs.startingCapital}
                    />
                    <MetricCard
                      icon={faChartLine} label="Median Max DD"
                      value={fmtPct(results.monteCarlo.maxDrawdownP50)}
                      sub="Across 1,000 simulations"
                      negative={results.monteCarlo.maxDrawdownP50 > 0.3}
                    />
                  </div>

                  {/* Monte Carlo chart */}
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 space-y-4">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <FontAwesomeIcon icon={faDice} className="text-[#9d4edd] text-sm" />
                      Monte Carlo Simulation
                      <span className="text-xs font-normal text-gray-500 ml-1">1,000 runs</span>
                    </h2>
                    <MonteCarloChart results={results} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Education Section ─────────────────────────────────────────── */}
      <section
        ref={revEducation.ref}
        className={`py-20 px-4 sm:px-6 border-t border-white/[0.06] ${revEducation.className}`}
      >
        <div className="max-w-4xl mx-auto space-y-12">
          <h2 className="text-[22px] font-semibold text-white text-center">
            What these metrics mean
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: 'Expectancy',
                body: 'The average amount you expect to earn per dollar risked. Positive expectancy means the strategy makes money over a large number of trades. Calculated as: (Win Rate x Reward) - (Loss Rate x Risk).',
              },
              {
                title: 'Profit Factor',
                body: 'Gross profits divided by gross losses. A profit factor above 1.0 means the strategy is profitable. Above 1.5 is generally considered strong. The break-even win rate shows the minimum win rate needed for this ratio.',
              },
              {
                title: 'Kelly Criterion',
                body: 'The theoretically optimal fraction of capital to risk per trade. In practice, most traders use Half-Kelly or less to reduce volatility. Going above Kelly dramatically increases the chance of large drawdowns.',
              },
              {
                title: 'Probability of Ruin',
                body: 'The chance that your equity drops below 10% of the starting capital at any point during the simulation. Even profitable strategies can blow up if position sizing is too aggressive relative to the edge.',
              },
              {
                title: 'Monte Carlo Simulation',
                body: 'Runs 1,000 independent simulations of your strategy, each with random trade ordering. The chart shows the range of possible equity paths from worst case (5th percentile) to best case (95th percentile).',
              },
              {
                title: 'Maximum Drawdown',
                body: 'The largest peak-to-trough decline in equity during a simulation. The median max drawdown tells you what to realistically expect. Actual drawdowns will sometimes be worse than the median.',
              },
            ].map(({ title, body }) => (
              <div key={title} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-2">
                <h3 className="text-sm font-semibold text-white">{title}</h3>
                <p className="text-sm text-gray-400/85 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ───────────────────────────────────────────────── */}
      <section
        ref={revCta.ref}
        className={`py-20 px-4 sm:px-6 border-t border-white/[0.06] ${revCta.className}`}
      >
        <div className="max-w-2xl mx-auto text-center space-y-5">
          <h2 className="text-2xl font-bold text-white">
            Ready to test your strategy on real market data?
          </h2>
          <p className="text-gray-400/85">
            Quanthop lets you write strategies in code, backtest on historical data,
            optimize parameters, and validate with Walk-Forward Analysis.
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-sm font-semibold transition-colors"
          >
            See Pricing
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </a>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
